import { StatusCode } from "api/protocols";

import Card from "components/card/Card";
import Page from "components/page/Page";
import { deriveTableShortTitle, isEmpty, isPositiveInteger } from "helpers/Helpers";
import { isEqual } from "lodash";
import React, { useEffect, useState } from "react";
import { showError } from "redux/actions/ui";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import { useParams } from "react-router";

import { useTranslation } from "react-i18next";
import {
  DeleteFloorPlanTemplate,
  GetFloorPlanTemplate,
  GetTable,
  GetTableElements,
  IFloorPlanTable,
  IFloorPlanTemplate,
  ITable,
  ITableElement,
  PostTable,
  PutArchiveTable,
  PutFloorPlanTemplate,
  PutTable,
} from "api/rpc/2024-04/facilityAdmin/tables/tables";
import FloorPlanTable from "../../floorPlanTable/FloorPlanTable";
import FloorPlan, { IFloorPlanBoundingRectangle } from "../../floorPlan/FloorPlan";
import { useAppDispatch } from "hooks/redux";

interface IFloorPlanTemplateEditState {
  templateId: number;

  floorPlan: IFloorPlanTemplate;
  floorPlanBeforeChanges: IFloorPlanTemplate;
  floorPlanLoaded: boolean;

  floorPlanTableElements: ITableElement[];
  floorPlanTableElementsLoaded: boolean;

  floorPlanTables: IFloorPlanTable[];
  floorPlanTablesBeforeChanges: IFloorPlanTable[];
  floorPlanTablesLoaded: boolean;

  floorPlanGridWidthInput: string;
  floorPlanGridHeightInput: string;

  floorPlanTablesBoundingRectangles: IFloorPlanBoundingRectangle[];
}

interface IParams {
  templateId: string;
}

export const ItemTypes = {
  TABLE: "table",
};

export const DEFAULT_TABLE_DIMENSION = 40;

export default function FloorPlanTemplateEdit() {
  const dispatch = useAppDispatch();
  const { templateId } = useParams<IParams>();

  const { t } = useTranslation();
  const MAX_GRID_DIMENSION = 75;
  const MIN_GRID_DIMENSION = 10;

  const [floorPlanTemplateEditState, setFloorPlanEditState] = useState<IFloorPlanTemplateEditState>({
    templateId: isPositiveInteger(templateId) ? parseInt(templateId) : undefined,

    floorPlan: undefined,
    floorPlanBeforeChanges: undefined,
    floorPlanLoaded: false,

    floorPlanTableElements: [],
    floorPlanTableElementsLoaded: false,

    floorPlanTables: [],
    floorPlanTablesBeforeChanges: undefined,
    floorPlanTablesLoaded: false,

    floorPlanGridWidthInput: "",
    floorPlanGridHeightInput: "",

    floorPlanTablesBoundingRectangles: [],
  });

  useEffect(() => {
    loadFloorPlanEdit();
  }, []);

  function loadFloorPlanEdit() {
    if (!floorPlanTemplateEditState.templateId) {
      dispatch(showError(t("Invalid floor plan id")));
    } else {
      void loadFloorPlanTableElements();
      void loadFloorPlanTables();
      void loadFloorPlan();
    }
  }

  async function loadFloorPlanTableElements() {
    const getFloorPlanTableElementsResponse = await GetTableElements(true);

    if (getFloorPlanTableElementsResponse.status !== StatusCode.OK) {
      dispatch(showError(t("Failed to load floor plan table elements")));
      return;
    }

    setFloorPlanEditState(prev => ({
      ...prev,
      floorPlanTableElements: getFloorPlanTableElementsResponse.data,
      floorPlanTableElementsLoaded: true,
    }));
  }

  function toFloorPlanTables(tables: ITable[]): IFloorPlanTable[] {
    if (tables.length === 0 || tables === undefined || tables === null) {
      return [];
    } else {
      const floorPlanTables: IFloorPlanTable[] = tables.map(table => {
        const floorPlanTable: IFloorPlanTable = {
          id: String(table.id),
          x_coordinate: table.x_coordinate,
          y_coordinate: table.y_coordinate,
          seats: table.seats,
          title: table.title,
          short_title: deriveTableShortTitle(table.title) || table.short_title,
          table_element_id: table.table_element_id,
          template_id: Number(templateId),
          rotation: table.rotation,
        };

        return floorPlanTable;
      });

      return floorPlanTables;
    }
  }

  async function loadFloorPlanTables() {
    const getFloorPlanTablesResponse = await GetTable({ template_id: floorPlanTemplateEditState.templateId }, true);

    if (getFloorPlanTablesResponse.status !== StatusCode.OK || getFloorPlanTablesResponse.data === undefined) {
      dispatch(showError(t("Failed to load floor plan tables")));
    } else {
      const loadedFloorPlanTables = toFloorPlanTables(getFloorPlanTablesResponse.data);

      setFloorPlanEditState(prev => ({
        ...prev,
        floorPlanTables: loadedFloorPlanTables,
        floorPlanTablesLoaded: true,
      }));
    }
  }

  async function loadFloorPlan() {
    const getFloorPlanResponse = await GetFloorPlanTemplate({ id: floorPlanTemplateEditState.templateId }, true);

    if (getFloorPlanResponse.status !== StatusCode.OK || getFloorPlanResponse.data?.[0] === undefined) {
      dispatch(showError(t("Failed to load floor plan")));
    } else {
      const floorPlan = getFloorPlanResponse.data[0];

      setFloorPlanEditState(prev => ({
        ...prev,
        floorPlan: {
          ...floorPlan,
        },
        floorPlanLoaded: true,
        floorPlanGridWidthInput: String(floorPlan.grid_width),
        floorPlanGridHeightInput: String(floorPlan.grid_height),
      }));
    }
  }

  async function deleteTemplate() {
    const deleteTemplateRes = await DeleteFloorPlanTemplate({ id: Number(templateId) }, true);
    if (deleteTemplateRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting template")));
      return;
    }

    history.back();
  }

  function isGridDimensionInBounds(value: string) {
    if (isPositiveInteger(value)) {
      const dimension = parseInt(value);

      return dimension >= MIN_GRID_DIMENSION && dimension <= MAX_GRID_DIMENSION;
    } else {
      return false;
    }
  }

  function isValidGridWidthDimension(value: string) {
    if (isGridDimensionInBounds(value)) {
      return isValidGridDimension(parseInt(value), floorPlanTemplateEditState.floorPlan.grid_height);
    } else {
      return false;
    }
  }

  function isValidGridHeightDimension(value: string) {
    if (isGridDimensionInBounds(value)) {
      return isValidGridDimension(floorPlanTemplateEditState.floorPlan.grid_width, parseInt(value));
    } else {
      return false;
    }
  }

  function isValidGridDimension(width: number, height: number) {
    return !floorPlanTemplateEditState.floorPlanTablesBoundingRectangles.some(boundingRectangle => {
      const bottomRightGridCoordinate = boundingRectangle.bottomRight;

      return bottomRightGridCoordinate.x > width || bottomRightGridCoordinate.y > height;
    });
  }

  useEffect(() => {
    if (isValidGridWidthDimension(floorPlanTemplateEditState.floorPlanGridWidthInput)) {
      setFloorPlanEditState(prev => ({
        ...prev,
        floorPlan: {
          ...prev.floorPlan,
          grid_width: parseInt(floorPlanTemplateEditState.floorPlanGridWidthInput),
        },
      }));
    }
  }, [floorPlanTemplateEditState.floorPlanGridWidthInput]);

  useEffect(() => {
    if (isValidGridHeightDimension(floorPlanTemplateEditState.floorPlanGridHeightInput)) {
      setFloorPlanEditState(prev => ({
        ...prev,
        floorPlan: {
          ...prev.floorPlan,
          grid_height: parseInt(floorPlanTemplateEditState.floorPlanGridHeightInput),
        },
      }));
    }
  }, [floorPlanTemplateEditState.floorPlanGridHeightInput]);

  useEffect(() => {
    if (
      !floorPlanTemplateEditState.floorPlanLoaded ||
      !floorPlanTemplateEditState.floorPlanTablesLoaded ||
      floorPlanTemplateEditState.floorPlanBeforeChanges !== undefined ||
      floorPlanTemplateEditState.floorPlanTablesBeforeChanges !== undefined
    ) {
      return;
    }

    setFloorPlanEditState(prev => ({
      ...prev,
      floorPlanBeforeChanges: prev.floorPlan,
      floorPlanTablesBeforeChanges: prev.floorPlanTables,
    }));
  }, [
    floorPlanTemplateEditState.floorPlanLoaded,
    floorPlanTemplateEditState.floorPlanTablesLoaded,
    floorPlanTemplateEditState.floorPlanBeforeChanges,
    floorPlanTemplateEditState.floorPlanTablesBeforeChanges,
  ]);

  function unsavedChangesExist() {
    if (
      floorPlanTemplateEditState.floorPlanBeforeChanges === undefined ||
      floorPlanTemplateEditState.floorPlanTablesBeforeChanges === undefined
    ) {
      return false;
    }

    return (
      !isEqual(floorPlanTemplateEditState.floorPlanBeforeChanges, floorPlanTemplateEditState.floorPlan) ||
      !isEqual(floorPlanTemplateEditState.floorPlanTablesBeforeChanges, floorPlanTemplateEditState.floorPlanTables)
    );
  }

  function isUUID(value: string) {
    return value.length === 36 && !isPositiveInteger(value);
  }

  async function saveFloorPlanTables() {
    if (isEmpty(floorPlanTemplateEditState.floorPlan.title)) {
      dispatch(showError(t("Invalid floor plan title")));
      return;
    }

    const putFloorPlanResponse = await PutFloorPlanTemplate(
      {
        id: floorPlanTemplateEditState.floorPlan.id,
        title: floorPlanTemplateEditState.floorPlan.title,
        grid_width: floorPlanTemplateEditState.floorPlan.grid_width,
        grid_height: floorPlanTemplateEditState.floorPlan.grid_height,
      },
      true,
    );

    if (putFloorPlanResponse.status !== StatusCode.OK) {
      dispatch(showError(t("Failed to update floor plan")));
      return;
    }

    const tablesToUpdate = floorPlanTemplateEditState.floorPlanTables.filter(table => !isUUID(table.id));
    const tablesToCreate = floorPlanTemplateEditState.floorPlanTables.filter(table => isUUID(table.id));
    const tablesToArchive = floorPlanTemplateEditState.floorPlanTablesBeforeChanges.filter(
      initialTable => !floorPlanTemplateEditState.floorPlanTables.some(table => table.id === initialTable.id),
    );

    let updatedTables: IFloorPlanTable[] = [];
    let createdTables: IFloorPlanTable[] = [];

    if (tablesToUpdate.length > 0) {
      const putFloorPlanTablesResponse = await PutTable({ tables: tablesToUpdate }, true);

      if (putFloorPlanTablesResponse.status !== StatusCode.OK) {
        dispatch(showError(t("Failed to update floor plan tables")));
        return;
      }

      updatedTables = toFloorPlanTables(putFloorPlanTablesResponse.data);
    }

    if (tablesToCreate.length > 0) {
      const postFloorPlanTablesResponse = await PostTable(
        {
          tables: tablesToCreate.map(table => {
            const { ...remainder } = table;
            const postTable: Omit<IFloorPlanTable, "id"> = remainder;

            return postTable;
          }),
        },
        true,
      );

      if (postFloorPlanTablesResponse.status !== StatusCode.OK) {
        dispatch(showError(t("Failed to create new tables")));
        return;
      }

      createdTables = toFloorPlanTables(postFloorPlanTablesResponse.data);
    }

    if (tablesToArchive.length > 0) {
      const archiveFloorPlanTablesResponse = await PutArchiveTable(
        {
          table_ids: tablesToArchive.map(table => parseInt(table.id)),
        },
        true,
      );

      if (archiveFloorPlanTablesResponse.status !== StatusCode.OK) {
        dispatch(showError(t("Failed to delete tables")));
        return;
      }
    }

    const updatedFloorPlanTables: IFloorPlanTable[] = [...updatedTables, ...createdTables];

    setFloorPlanEditState(prev => ({
      ...prev,
      floorPlanBeforeChanges: prev.floorPlan,
      floorPlanTables: updatedFloorPlanTables,
      floorPlanTablesBeforeChanges: updatedFloorPlanTables,
      floorPlanGridWidthInput: String(prev.floorPlan.grid_width),
      floorPlanGridHeightInput: String(prev.floorPlan.grid_height),
    }));
  }

  function cancelUnsavedChanges() {
    setFloorPlanEditState(prev => ({
      ...prev,
      floorPlan: prev.floorPlanBeforeChanges,
      floorPlanTables: prev.floorPlanTablesBeforeChanges,
      floorPlanGridWidthInput: String(prev.floorPlanBeforeChanges.grid_width),
      floorPlanGridHeightInput: String(prev.floorPlanBeforeChanges.grid_height),
    }));
  }

  function setFloorPlanTables(updateTables: (prevTables: IFloorPlanTable[]) => IFloorPlanTable[]): void {
    setFloorPlanEditState(prev => ({ ...prev, floorPlanTables: updateTables(prev.floorPlanTables) }));
  }

  function setFloorPlanTablesBoundingRectangles(boundingRectangles: IFloorPlanBoundingRectangle[]) {
    setFloorPlanEditState(prev => ({ ...prev, floorPlanTablesBoundingRectangles: boundingRectangles }));
  }

  function setFloorPlanTitle(e: React.ChangeEvent<HTMLInputElement>) {
    setFloorPlanEditState(prev => ({ ...prev, floorPlan: { ...prev.floorPlan, title: e.target.value } }));
  }

  return (
    <Page
      title={t("Floor Plan Template")}
      subtitle={t("Edit your floor plan template")}
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("Floor Plan Templates"),
          url: "/admin/settings/floor-plan-template",
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveFloorPlanTables,
        onCancel: cancelUnsavedChanges,
      }}
      secondaryActions={[
        {
          content: t("Delete"),
          action: () => deleteTemplate(),
        },
      ]}
    >
      <Card>
        <Card.Section title={t("Floor Plan Settings")}>
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("Title")}
                value={floorPlanTemplateEditState?.floorPlan?.title}
                onChange={e => setFloorPlanTitle(e)}
                error={isEmpty(floorPlanTemplateEditState?.floorPlan?.title)}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                label={t("Grid Width")}
                value={floorPlanTemplateEditState.floorPlanGridWidthInput}
                onChange={(e: any) =>
                  setFloorPlanEditState(prev => ({ ...prev, floorPlanGridWidthInput: e.target.value }))
                }
                error={!isValidGridWidthDimension(floorPlanTemplateEditState.floorPlanGridWidthInput)}
              />
              <Input
                label={t("Grid Height")}
                value={floorPlanTemplateEditState.floorPlanGridHeightInput}
                onChange={(e: any) =>
                  setFloorPlanEditState(prev => ({ ...prev, floorPlanGridHeightInput: e.target.value }))
                }
                error={!isValidGridHeightDimension(floorPlanTemplateEditState.floorPlanGridHeightInput)}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>

      {floorPlanTemplateEditState.floorPlanTableElementsLoaded &&
        floorPlanTemplateEditState.floorPlanTablesLoaded &&
        floorPlanTemplateEditState.floorPlanLoaded && (
          <Card style={{ marginBottom: "320px" }}>
            <Card.Section title={t("Floor Plan")}>
              <DndProvider backend={HTML5Backend}>
                <div className="floor-plan-view">
                  <div
                    className="floor-plan-view-table-elements"
                    style={{
                      gridTemplateColumns: `repeat(auto-fill, minmax(${DEFAULT_TABLE_DIMENSION}px, 1fr))`,
                    }}
                  >
                    {floorPlanTemplateEditState.floorPlanTableElements.map((element, i) => {
                      return (
                        <div key={`option-${i}`}>
                          <FloorPlanTable optionTable={{ element }} />
                        </div>
                      );
                    })}
                  </div>
                  <FloorPlan
                    tables={floorPlanTemplateEditState.floorPlanTables}
                    setTables={setFloorPlanTables}
                    tableElements={floorPlanTemplateEditState.floorPlanTableElements}
                    templateId={floorPlanTemplateEditState.templateId}
                    gridWidth={floorPlanTemplateEditState.floorPlan.grid_width}
                    gridHeight={floorPlanTemplateEditState.floorPlan.grid_height}
                    tablesBoundingRectangles={floorPlanTemplateEditState.floorPlanTablesBoundingRectangles}
                    setTablesBoundingRectangles={setFloorPlanTablesBoundingRectangles}
                  />
                </div>
              </DndProvider>
            </Card.Section>
          </Card>
        )}
    </Page>
  );
}

/**
 *  Returns null if negative number.
 *  NULL kitchen_location_id defaults to -1 ("None")
 *  - Convert "None" kitchen_location_id to NULL before PUT call.
 */
export const negativeCheckHelper = (value: number) => {
  return value < 0 ? null : value;
};
